import type {
  UploadTaskFileInput,
  UploadTaskFolderInput,
} from '@/api/modules/material/uploadTask.types';
import type { UploadTaskFile } from './types';

/**
 * 组装「创建上传任务」接口的明细入参。
 *
 * 契约要点（见 `temp.local/material-upload-task-frontend-contract.md`）：
 * - 创建任务时文件**还没上传 OSS**，所以入参只有 `clientFileId + fileName`，不含 ossKey；
 * - 素材类型（taskType=1）传扁平 `files`；
 * - 文件夹类型（taskType=2）传 `folders` 树，**空目录用 `files: []` 表达**，
 *   目录树由后端在 create 时预建，重试落原目录不会重复建文件夹。
 *
 * 本文件是纯函数，方便单测。
 */

/** 目录路径规范化：统一 `/` 分隔、去掉首尾斜杠 */
const normalizePath = (path: string) => path.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');

/** 文件展示名：优先用持久化的 `fileName`（刷新后 `file` 句柄会被剥离） */
const getFileName = (file: UploadTaskFile) => file.fileName || file.file?.name || '未知文件';

/**
 * 解析文件所属目录路径。
 *
 * 正常链路上 `folderPath` 一定非空（上传文件夹只允许一次传一个父文件夹，
 * `webkitRelativePath` 必然带父目录名）；这里用 `relativePath` 的目录部分兜底，
 * 避免极端情况下文件被静默丢掉。
 */
const resolveFolderPath = (file: UploadTaskFile): string => {
  const fromFolder = normalizePath(file.folderPath || '');
  if (fromFolder) return fromFolder;
  const parts = normalizePath(file.relativePath || '')
    .split('/')
    .filter(Boolean);
  return parts.length > 1 ? parts.slice(0, -1).join('/') : '';
};

interface PendingNode {
  folderName: string;
  files: UploadTaskFileInput[];
  children: PendingNode[];
}

/** 按路径逐级建（或复用）节点，返回最末级节点；空路径返回 undefined */
const ensureNode = (
  path: string,
  roots: PendingNode[],
  map: Map<string, PendingNode>
): PendingNode | undefined => {
  const parts = path.split('/').filter(Boolean);
  if (!parts.length) return undefined;

  let children = roots;
  let current: PendingNode | undefined;
  let currentPath = '';

  for (const part of parts) {
    currentPath = currentPath ? `${currentPath}/${part}` : part;
    let node = map.get(currentPath);
    if (!node) {
      node = { folderName: part, files: [], children: [] };
      map.set(currentPath, node);
      children.push(node);
    }
    current = node;
    children = node.children;
  }

  return current;
};

/** 去掉内部结构，转成接口入参（`sortOrder` 按同级顺序从 1 开始） */
const toFolderInput = (nodes: PendingNode[]): UploadTaskFolderInput[] =>
  nodes.map((node, index) => ({
    folderName: node.folderName,
    sortOrder: index + 1,
    files: node.files,
    children: toFolderInput(node.children),
  }));

/** 素材类型：扁平文件列表 */
export const buildUploadTaskFiles = (files: UploadTaskFile[]): UploadTaskFileInput[] =>
  files.map((file) => ({ clientFileId: file.uid, fileName: getFileName(file) }));

/**
 * 文件夹类型：目录树。
 * 先用 `folderPaths` 建好骨架（**空目录也会被保留**），再把文件按自己的目录落位。
 */
export const buildUploadTaskFolders = (
  files: UploadTaskFile[],
  folderPaths: string[] = []
): UploadTaskFolderInput[] => {
  const roots: PendingNode[] = [];
  const map = new Map<string, PendingNode>();

  folderPaths.forEach((folderPath) => {
    ensureNode(normalizePath(folderPath), roots, map);
  });

  files.forEach((file) => {
    const node = ensureNode(resolveFolderPath(file), roots, map);
    if (!node) {
      console.warn('[material-upload] 文件缺少目录路径，已跳过 create 入参组装', file.uid);
      return;
    }
    node.files.push({ clientFileId: file.uid, fileName: getFileName(file) });
  });

  return toFolderInput(roots);
};
